/**
 * Verbose mode — the network-wide 'Shared' cost-telemetry toggle.
 *
 * Controls whether a run block shows the per-stage COST BREAKDOWN (per-agent model/token/$ chips,
 * "All Leads (routing)", "All Reducers (judging)") or just the single headline total in the header.
 *
 * DEFAULT **OFF** — deliberately the mirror image of ``economyMode`` (which defaults ON). Cost
 * plumbing is developer telemetry: useful while tuning a network, noise for someone reading an
 * answer. So a room is quiet out of the box and the user OPTS IN.
 *
 *   - unset / absent / null / undefined  → OFF (default)
 *   - explicit opt-in ('on'/'true'/'1'/'yes' or bool true) → ON
 *   - anything else → OFF
 *
 * NOTE the asymmetry with ``economyMode.isEconomyOn``: that one treats an UNKNOWN value as ON
 * (fail-cheap). Here an unknown value is OFF (fail-quiet) — a garbled flag must not suddenly
 * surface cost chrome to an end user.
 */

const ON_TOKENS = new Set(['on', 'true', '1', 'yes']);

/**
 * @param {unknown} raw The ``verbose`` value off a __roomContext__ row (bool | string | nullish).
 * @returns {boolean} Whether verbose mode is active (default false).
 */
export function isVerboseOn(raw) {
  if (raw == null) return false;                 // unset → default OFF
  if (typeof raw === 'boolean') return raw;
  return ON_TOKENS.has(String(raw).trim().toLowerCase());
}

/**
 * Resolve verbose mode from a network summary's agents map (the room path).
 * Reads the reserved ``__roomContext__`` Shared row; default OFF when the row/flag is absent.
 *
 * @param {Record<string, unknown> | null | undefined} agents The network's agents map.
 * @returns {boolean}
 */
export function verboseFromAgents(agents) {
  const rc = agents && typeof agents === 'object' ? agents.__roomContext__ : null;
  const raw = rc && typeof rc === 'object' ? rc.verbose : undefined;
  return isVerboseOn(raw);
}
